// Modal do nó Gemini (casca Aeros): mesmos campos, validação Yup e payload
// `typebotIntegration` do modal original — só a apresentação mudou.
// Textos comuns reaproveitam aeros.flow.modals.openai.*; só o que é
// específico do Gemini fica em aeros.flow.modals.gemini.*.
import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Field, FieldArray } from "formik";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Switch from "@material-ui/core/Switch";
import Radio from "@material-ui/core/Radio";
import { Visibility, VisibilityOff, Add, Close } from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import AerosDialog from "../AerosDialog";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import useAerosSettingsStyles from "../Settings/aerosSettingsStyles";
import { useAiModalStyles } from "../FlowBuilderAddOpenAIModal";

const T = (k, o) => i18n.t(`aeros.flow.modals.openai.${k}`, o);
const TG = (k) => i18n.t(`aeros.flow.modals.gemini.${k}`);

// Lista de modelos Gemini suportados
const geminiModels = ["gemini-pro", "gemini-1.5-pro", "gemini-1.5-flash", "gemini-2.0-flash", "gemini-2.0-pro"];

const DEFAULT_KEYWORDS = ["continuar", "próximo", "avançar"];

// Validação (mensagens resolvidas no idioma atual, por isso é função)
const buildSchema = () =>
  Yup.object().shape({
    name: Yup.string().min(5, T("errors.tooShort")).max(100, T("errors.tooLong")).required(T("errors.required")),
    prompt: Yup.string().min(50, T("errors.tooShort")).required(T("errors.promptRequired")),
    model: Yup.string().oneOf(geminiModels, T("errors.invalidModel")).required(T("errors.modelRequired")),
    maxTokens: Yup.number()
      .min(10, T("errors.minTokens", { n: 10 }))
      .max(8000, T("errors.maxTokens", { n: 8000 }))
      .required(T("errors.required")),
    temperature: Yup.number().min(0, T("errors.min", { n: 0 })).max(2, T("errors.max", { n: 2 })).required(T("errors.required")),
    apiKey: Yup.string().required(T("errors.apiKeyRequired")),
    maxMessages: Yup.number().min(1, T("errors.min", { n: 1 })).max(100, T("errors.max", { n: 100 })).required(T("errors.required")),
    flowMode: Yup.string().oneOf(["permanent", "temporary"]).required(T("errors.required")),
    maxInteractions: Yup.number().when("flowMode", {
      is: "temporary",
      then: Yup.number().min(1, T("errors.min", { n: 1 })).max(50, T("errors.max", { n: 50 })).nullable(),
      otherwise: Yup.number().nullable(),
    }),
    completionTimeout: Yup.number().when("flowMode", {
      is: "temporary",
      then: Yup.number().min(1, T("errors.min", { n: 1 })).max(60, T("errors.max", { n: 60 })).nullable(),
      otherwise: Yup.number().nullable(),
    }),
    continueKeywords: Yup.array().when("flowMode", {
      is: "temporary",
      then: Yup.array().of(Yup.string().required(T("errors.keywordEmpty"))).min(1, T("errors.keywordsMin")),
      otherwise: Yup.array(),
    }),
    objective: Yup.string().when(["flowMode", "autoCompleteOnObjective"], {
      is: (flowMode, autoComplete) => flowMode === "temporary" && autoComplete,
      then: Yup.string().required(T("errors.objectiveRequired")),
      otherwise: Yup.string(),
    }),
  });

const initialState = {
  name: "",
  prompt: "",
  model: "gemini-1.5-flash",
  maxTokens: 1000,
  temperature: 0.7,
  apiKey: "",
  maxMessages: 10,
  queueId: 0,
  // Campos para controle de fluxo
  flowMode: "permanent",
  maxInteractions: 5,
  completionTimeout: 10,
  continueKeywords: DEFAULT_KEYWORDS,
  objective: "",
  autoCompleteOnObjective: false,
};

const modelNames = {
  "gemini-pro": "Gemini Pro",
  "gemini-1.5-pro": "Gemini 1.5 Pro",
  "gemini-1.5-flash": "Gemini 1.5 Flash",
  "gemini-2.0-flash": "Gemini 2.0 Flash",
  "gemini-2.0-pro": "Gemini 2.0 Pro",
};

const FlowBuilderGeminiModal = ({ open, onSave, data, onUpdate, close }) => {
  const classes = useAiModalStyles();
  const m = useAerosModalStyles();
  const aeros = useAerosSettingsStyles();
  const [showApiKey, setShowApiKey] = useState(false);
  const [integration, setIntegration] = useState(initialState);
  const [newKeyword, setNewKeyword] = useState("");

  useEffect(() => {
    if (open === "edit") {
      const typebotIntegration = data?.data?.typebotIntegration || {};
      setIntegration({
        ...initialState,
        ...typebotIntegration,
        model: geminiModels.includes(typebotIntegration.model) ? typebotIntegration.model : "gemini-1.5-flash",
        flowMode: typebotIntegration.flowMode || "permanent",
        continueKeywords: typebotIntegration.continueKeywords || DEFAULT_KEYWORDS,
        maxInteractions: typebotIntegration.maxInteractions || 5,
        completionTimeout: typebotIntegration.completionTimeout || 10,
        objective: typebotIntegration.objective || "",
        autoCompleteOnObjective: typebotIntegration.autoCompleteOnObjective || false,
      });
    } else if (open === "create") {
      setIntegration(initialState);
    }
  }, [open, data]);

  const handleClose = () => {
    setNewKeyword("");
    close(null);
  };

  const handleSavePrompt = (values, { setSubmitting }) => {
    const promptData = {
      ...values,
      // Garantir que campos do modo temporário sejam nulos se modo for permanente
      maxInteractions: values.flowMode === "temporary" ? values.maxInteractions : null,
      completionTimeout: values.flowMode === "temporary" ? values.completionTimeout : null,
      continueKeywords: values.flowMode === "temporary" ? values.continueKeywords : [],
      objective: values.flowMode === "temporary" ? values.objective : "",
      autoCompleteOnObjective: values.flowMode === "temporary" ? values.autoCompleteOnObjective : false,
      // Forçar provider como gemini e voz como texto
      provider: "gemini",
      voice: "texto",
    };

    if (open === "edit") {
      onUpdate({ ...data, data: { typebotIntegration: promptData } });
    } else if (open === "create") {
      promptData.projectName = promptData.name;
      onSave({ typebotIntegration: promptData });
    }
    handleClose();
    setSubmitting(false);
  };

  const addKeyword = (arrayHelpers, values, keyword) => {
    if (keyword.trim() && !values.continueKeywords.includes(keyword.trim())) {
      arrayHelpers.push(keyword.trim());
      setNewKeyword("");
    }
  };

  // Campo padrão: rótulo acima + TextField outlined `m.input`, erro/dica abaixo.
  const field = (form, name, label, hintKey, props = {}) => {
    const err = form.touched[name] && form.errors[name];
    return (
      <div>
        <label className={m.fieldLabel}>{label}</label>
        <Field as={TextField} name={name} variant="outlined" className={m.input} error={Boolean(err)} {...props} />
        {err ? <div className={classes.error}>{err}</div> : hintKey ? <div className={m.hint}>{T(hintKey)}</div> : null}
      </div>
    );
  };

  const modeBox = (form, value, titleKey, descKey) => (
    <div
      className={`${m.optionBox} ${classes.modeBox} ${form.values.flowMode === value ? classes.modeOn : ""}`}
      onClick={() => form.setFieldValue("flowMode", value)}
    >
      <Radio color="primary" checked={form.values.flowMode === value} value={value} name="flowMode" size="small" />
      <div>
        <div className={m.optionTitle}>{T(titleKey)}</div>
        <div className={m.optionDesc}>{T(descKey)}</div>
      </div>
    </div>
  );

  return (
    <Formik initialValues={integration} enableReinitialize validationSchema={buildSchema} onSubmit={handleSavePrompt}>
      {(form) => (
        <AerosDialog
          open={open === "create" || open === "edit"}
          onClose={handleClose}
          width={640}
          title={TG(open === "edit" ? "editTitle" : "title")}
          subtitle={TG("subtitle")}
          onSubmit={form.submitForm}
          submitLabel={i18n.t(open === "edit" ? "aeros.flow.save" : "aeros.flow.add")}
          loading={form.isSubmitting}
        >
          {/* CONFIGURAÇÕES BÁSICAS */}
          <div className={m.section}>
            <div className={m.sectionHead}>{TG("basic")}</div>
            <div className={classes.sectionBody}>
              {field(form, "name", T("name"), null, { required: true })}
              {field(form, "apiKey", TG("apiKey"), null, {
                type: showApiKey ? "text" : "password",
                InputProps: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setShowApiKey(!showApiKey)}>
                        {showApiKey ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              })}
              {field(form, "model", TG("model"), null, {
                select: true,
                children: geminiModels.map((model) => (
                  <MenuItem key={model} value={model}>
                    {modelNames[model] || model}
                  </MenuItem>
                )),
              })}
              {field(form, "prompt", T("prompt"), null, { multiline: true, minRows: 6, placeholder: T("promptPlaceholder") })}
              <div className={classes.grid3}>
                {field(form, "temperature", T("temperature"), "temperatureHint", {
                  type: "number",
                  inputProps: { step: "0.1", min: "0", max: "2" },
                })}
                {field(form, "maxTokens", T("maxTokens"), "maxTokensHint", { type: "number" })}
                {field(form, "maxMessages", T("maxMessages"), "maxMessagesHint", { type: "number" })}
              </div>
            </div>
          </div>

          {/* COMPORTAMENTO DO FLUXO */}
          <div className={m.section}>
            <div className={m.sectionHead}>{T("behavior")}</div>
            <div className={classes.sectionBody}>
              <div className={m.grid2}>
                {modeBox(form, "permanent", "permanent", "permanentDesc")}
                {modeBox(form, "temporary", "temporary", "temporaryDesc")}
              </div>

              {form.values.flowMode === "temporary" && (
                <>
                  <div className={m.hint}>{T("temporaryHint")}</div>
                  <div className={m.grid2}>
                    {field(form, "maxInteractions", T("maxInteractions"), "maxInteractionsHint", {
                      type: "number",
                      inputProps: { min: 0, max: 50 },
                    })}
                    {field(form, "completionTimeout", T("completionTimeout"), "completionTimeoutHint", {
                      type: "number",
                      inputProps: { min: 0, max: 60 },
                    })}
                  </div>

                  <FieldArray name="continueKeywords">
                    {(arrayHelpers) => (
                      <div>
                        <label className={m.fieldLabel}>{T("keywords")}</label>
                        <div className={classes.keywordRow}>
                          <TextField
                            variant="outlined"
                            className={`${m.input} ${m.inputSmall}`}
                            placeholder={T("keywordPlaceholder")}
                            value={newKeyword}
                            onChange={(e) => setNewKeyword(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                addKeyword(arrayHelpers, form.values, newKeyword);
                              }
                            }}
                          />
                          <button
                            type="button"
                            className={m.linkButton}
                            style={{ marginLeft: 0, whiteSpace: "nowrap" }}
                            onClick={() => addKeyword(arrayHelpers, form.values, newKeyword)}
                            disabled={!newKeyword.trim()}
                          >
                            <Add /> {i18n.t("aeros.flow.add")}
                          </button>
                        </div>
                        <div className={classes.keywords} style={{ marginTop: 8 }}>
                          {form.values.continueKeywords.map((keyword, index) => (
                            <span key={index} className={`${m.chip} ${classes.keywordChip}`}>
                              {keyword}
                              <Close onClick={() => arrayHelpers.remove(index)} />
                            </span>
                          ))}
                        </div>
                        <div className={m.hint}>{T("keywordsHint")}</div>
                        {form.touched.continueKeywords && typeof form.errors.continueKeywords === "string" && (
                          <div className={classes.error}>{form.errors.continueKeywords}</div>
                        )}
                      </div>
                    )}
                  </FieldArray>

                  {field(form, "objective", T("objective"), "objectiveHint", {
                    multiline: true,
                    minRows: 2,
                    placeholder: T("objectivePlaceholder"),
                  })}

                  <div className={m.optionBox}>
                    <div style={{ flex: 1 }}>
                      <div className={m.optionTitle}>{T("autoComplete")}</div>
                      <div className={m.optionDesc}>{T("autoCompleteDesc")}</div>
                    </div>
                    <Switch
                      color="primary"
                      className={aeros.toggleSwitchSmall}
                      checked={form.values.autoCompleteOnObjective}
                      onChange={(e) => form.setFieldValue("autoCompleteOnObjective", e.target.checked)}
                      name="autoCompleteOnObjective"
                    />
                  </div>

                  {field(form, "queueId", T("queueId"), "queueIdHint", { type: "number", inputProps: { min: 0 } })}
                </>
              )}
            </div>
          </div>
        </AerosDialog>
      )}
    </Formik>
  );
};

export default FlowBuilderGeminiModal;
